import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress
} from '@mui/material';
import { apiService } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';

const ChecklistStatusSection = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStatuses, setUserStatuses] = useState([]);
  const [displayMonth, setDisplayMonth] = useState('');
  const { user } = useAuth();

  // Helper function to get the target month and year - FIXED to match compliance data logic
  const getTargetMonthAndYear = () => {
    const now = new Date();
    const currentMonth = now.getMonth() + 1; // 0-indexed to 1-indexed
    const currentYear = now.getFullYear();

    // Always show current month to match compliance data logic
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    setDisplayMonth(`${monthNames[currentMonth - 1]} ${currentYear}`);
    
    return { month: currentMonth, year: currentYear };
  };

  useEffect(() => {
    const fetchUserStatuses = async () => {
      try {
        // Don't set loading to true on refreshes to avoid UI flickering
        if (userStatuses.length === 0) {
          setLoading(true);
        }
        
        // Get dashboard data which includes compliance information
        const dashboardData = await apiService.getAdminDashboardData();
        
        if (!dashboardData || !dashboardData.complianceData) {
          throw new Error('Failed to fetch compliance data');
        }
        
        // Get all users to display their status
        const users = await apiService.getUsersByBranch(user.branch);
        console.log('Users in branch:', users.length);
        
        // Determine which month's data to show based on current date
        const { month, year } = getTargetMonthAndYear();
        console.log(`Looking for checklists in ${month}/${year}`);
        
        // Get checklists for target month and year
        const checklists = await apiService.getChecklistsByMonth(month, year, user.branch);
        console.log('Branch-filtered checklists found:', checklists.length);
        
        // Additional client-side filtering as backup (should not be needed now)
        const branchChecklists = checklists.filter(c => c.branch === user.branch);
        console.log('Double-filtered checklists:', branchChecklists.length);
        console.log('Checklists:', branchChecklists.map(c => ({ user_id: c.user_id, date: c.date, branch: c.branch })));
        
        // Map users to their submission status
        const statusData = users.map(u => {
          const hasSubmitted = branchChecklists.some(c => c.user_id === u.id);
          console.log(`User ${u.username} (ID: ${u.id}): ${hasSubmitted ? 'Submitted' : 'Not Submitted'}`);
          return {
            id: u.id,
            name: u.username,
            email: u.email,
            status: hasSubmitted ? 'Submitted' : 'Not Submitted'
          };
        });
        
        setUserStatuses(statusData);
      } catch (err) {
        console.error('Error fetching user statuses:', err);
        setError('Failed to load user statuses');
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchUserStatuses();
    
    // Set up polling to refresh data every 8 seconds (matching ExpenseReportStatusSection)
    const refreshInterval = setInterval(() => {
      fetchUserStatuses();
    }, 8000);
    
    // Clean up interval on component unmount
    return () => clearInterval(refreshInterval);
  }, [user]);

  if (loading && userStatuses.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Checklist Status - {displayMonth}
      </Typography>
      
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userStatuses.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell align="right">
                  <Chip 
                    label={user.status} 
                    color={user.status === 'Submitted' ? 'success' : 'warning'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
            {loading && userStatuses.length > 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                    <CircularProgress size={20} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ChecklistStatusSection; 